import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'

const AiActionExplainer = ({ label = 'How this works', title, lead, rows = [], tip }) => {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  return (
    <Wrap>
      <InfoButton
        type='button'
        aria-label={title || label}
        aria-expanded={open}
        onClick={e => { e.stopPropagation(); setOpen(true) }}
      >
        <InfoOutlinedIcon style={{ fontSize: 15 }} />
        <span>{label}</span>
      </InfoButton>

      {open && createPortal(
        <Backdrop onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false) }}>
          <Dialog role='dialog' aria-modal='true' aria-label={title || label}>
            <Header>
              <HeaderTitle>{title}</HeaderTitle>
              <CloseBtn type='button' aria-label='Close' onClick={() => setOpen(false)}>
                <CloseRoundedIcon style={{ fontSize: 18 }} />
              </CloseBtn>
            </Header>

            <Body>
              {lead && <Lead>{lead}</Lead>}
              {rows.length > 0 && (
                <List>
                  {rows.map(([h, t]) => (
                    <RowItem key={h}>
                      <Dot />
                      <Text><strong>{h}.</strong> {t}</Text>
                    </RowItem>
                  ))}
                </List>
              )}
              {tip && <Tip>{tip}</Tip>}
            </Body>
          </Dialog>
        </Backdrop>,
        document.body
      )}
    </Wrap>
  )
}

export default AiActionExplainer

const Wrap = styled.span` display: inline-flex; `

const InfoButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: #fff;
  border: 1px solid #e4e7f0;
  color: ${({ theme }) => theme.colors.grey[800]};
  border-radius: 999px;
  padding: 4px 11px 4px 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 160ms ease, border-color 160ms ease;
  &:hover { background: #f4f4f6; border-color: #cfd6e4; }
`

const Backdrop = styled.div`
  position: fixed; inset: 0; z-index: 100070;
  background: rgba(12, 18, 41, 0.45);
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
`

const Dialog = styled.div`
  background: #fff; border-radius: 16px;
  width: 620px; max-width: 100%; max-height: 88vh;
  display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 24px 60px rgba(12, 18, 41, 0.28);
`

const Header = styled.div`
  flex-shrink: 0;
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  padding: 16px 20px; border-bottom: 1px solid #f0f0f2;
`

const HeaderTitle = styled.h4`
  margin: 0; font-size: 16px; font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const CloseBtn = styled.button`
  font: inherit; flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border: none; background: transparent;
  color: ${({ theme }) => theme.colors.grey[800]};
  border-radius: 8px; cursor: pointer; opacity: 0.7;
  &:hover { opacity: 1; background: rgba(12, 18, 41, 0.05); }
`

const Body = styled.div` overflow-y: auto; overflow-x: hidden; padding: 18px 20px 22px; `

const Lead = styled.p`
  margin: 0 0 16px; font-size: 13px; line-height: 1.55; color: #4d4d56;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const List = styled.div` display: flex; flex-direction: column; gap: 12px; `

const RowItem = styled.div` display: flex; gap: 9px; align-items: flex-start; `

const Dot = styled.span`
  flex: 0 0 auto; width: 9px; height: 9px; margin-top: 5px; border-radius: 50%;
  background: #33becc; box-shadow: 0 0 0 2px rgba(51, 190, 204, 0.2);
`

const Text = styled.span`
  font-size: 12.5px; line-height: 1.5; color: #4d4d56;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const Tip = styled.p`
  margin: 18px 0 0; padding: 11px 13px; border-radius: 10px;
  background: #f6fbfa; border: 1px solid #e6f3f1;
  font-size: 12.5px; line-height: 1.5; color: #2f4f49;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
  em { font-style: normal; font-weight: 800; }
`
